import { CLAIM_KINDS, CLAIM_STATUSES, type ClaimKind, type ClaimStatus } from '@beta-assur/shared';
import { useState, type FormEvent } from 'react';
import { Field } from '../components/page';
import { ActionError, Empty, QueryView } from '../components/states';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox, Input, Select, Textarea } from '../components/ui/input';
import { Table, TBody, TD, TR } from '../components/ui/table';
import { day } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { useApiMutation, useApiQuery } from '../lib/query';

// Sinistres déclarés (BA-077) : déclarations reçues de la page assuré ou saisies au portail
// (téléphone, agence), suivi de leur transmission à l'assureur.

interface Claim {
  id: string;
  reference: string;
  kind: ClaimKind;
  occurredOn: string;
  place: string;
  description: string | null;
  thirdParty: boolean;
  status: ClaimStatus;
  origin: 'page' | 'portal';
}

const TONE = { received: 'warning', forwarded: 'info', closed: 'neutral' } as const;

export function ClaimsCard({
  contractId,
  canDeclare,
  canFollow,
}: {
  contractId: string;
  canDeclare: boolean;
  canFollow: boolean;
}) {
  const { t, language } = useI18n();
  const path = `/v1/contracts/${contractId}/claims`;
  const query = useApiQuery<{ items: Claim[] }>(path);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    kind: 'accident' as ClaimKind,
    occurredOn: '',
    place: '',
    description: '',
    thirdParty: false,
  });
  const declare = useApiMutation<Record<string, unknown>>('POST', path, {
    invalidate: [path, '/v1/escalations'],
    onSuccess: () => {
      setOpen(false);
      setForm((f) => ({ ...f, occurredOn: '', place: '', description: '', thirdParty: false }));
    },
  });
  const follow = useApiMutation<{ id: string; status: ClaimStatus }>(
    'PATCH',
    (b) => `/v1/claims/${b.id}`,
    { invalidate: [path] },
  );

  function submit(event: FormEvent) {
    event.preventDefault();
    declare.mutate({
      kind: form.kind,
      occurredOn: form.occurredOn,
      place: form.place.trim(),
      thirdParty: form.thirdParty,
      ...(form.description.trim() ? { description: form.description.trim() } : {}),
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.claims.title}</CardTitle>
        {canDeclare && !open ? (
          <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
            {t.claims.declare}
          </Button>
        ) : null}
      </CardHeader>
      {open ? (
        <CardContent className="border-b border-border">
          <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label={t.claims.kind}>
              <Select
                value={form.kind}
                onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value as ClaimKind }))}
              >
                {CLAIM_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {t.claims.kinds[k]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t.claims.occurredOn}>
              <Input
                type="date"
                required
                value={form.occurredOn}
                onChange={(e) => setForm((f) => ({ ...f, occurredOn: e.target.value }))}
              />
            </Field>
            <Field label={t.claims.place}>
              <Input
                required
                maxLength={120}
                value={form.place}
                onChange={(e) => setForm((f) => ({ ...f, place: e.target.value }))}
              />
            </Field>
            <Field label={t.claims.description} hint={t.claims.noHealth} className="sm:col-span-3">
              <Textarea
                maxLength={1000}
                rows={3}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={form.thirdParty}
                onChange={(e) => setForm((f) => ({ ...f, thirdParty: e.target.checked }))}
              />
              {t.claims.thirdParty}
            </label>
            <div className="flex gap-2 sm:col-span-3">
              <Button type="submit" disabled={declare.isPending}>
                {t.claims.save}
              </Button>
              <Button variant="ghost" onClick={() => setOpen(false)}>
                {t.common.cancel}
              </Button>
            </div>
          </form>
          <ActionError error={declare.error} />
        </CardContent>
      ) : null}
      <QueryView query={query}>
        {(data) =>
          data.items.length === 0 ? (
            <Empty>{t.claims.none}</Empty>
          ) : (
            <Table>
              <TBody>
                {data.items.map((c) => (
                  <TR key={c.id}>
                    <TD className="font-mono text-xs">{c.reference}</TD>
                    <TD className="whitespace-nowrap">{day(c.occurredOn, language)}</TD>
                    <TD>
                      {t.claims.kinds[c.kind]}
                      {c.thirdParty ? ` (${t.claims.withThirdParty})` : ''}
                      <span className="block text-xs text-muted-foreground">
                        {c.place}
                        {c.description ? ` : ${c.description}` : ''}
                      </span>
                    </TD>
                    <TD>
                      {canFollow ? (
                        <Select
                          aria-label={`${t.claims.status} ${c.reference}`}
                          className="w-auto"
                          value={c.status}
                          onChange={(e) =>
                            follow.mutate({ id: c.id, status: e.target.value as ClaimStatus })
                          }
                        >
                          {CLAIM_STATUSES.map((s) => (
                            <option key={s} value={s}>
                              {t.claims.statuses[s]}
                            </option>
                          ))}
                        </Select>
                      ) : (
                        <Badge tone={TONE[c.status]}>{t.claims.statuses[c.status]}</Badge>
                      )}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )
        }
      </QueryView>
      <ActionError error={follow.error} />
    </Card>
  );
}
